import { useFormStore } from '../lib/store';
import { api } from '../lib/api';
import { validateField, FieldValidationConfig } from '../lib/validators';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { input as inputCls, inputError, textarea as textareaCls, label as labelCls, hint as hintCls } from './ui';
import { IconCamera, IconCheck, IconChevronDown, IconImage, IconSpinner } from './Icons';

export interface FormField {
  id: string;
  type: string;
  label: string;
  labelEn?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  options?: { value: string; label: string; labelEn?: string; price?: number; imageUrl?: string }[];
  validation?: {
    pattern?: string;
    patternMessage?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    minAge?: number;
    maxAge?: number;
    format?: 'taiwan_id' | 'resident_cert' | 'mobile' | 'phone' | 'email';
  };
  imageUrl?: string;
  imageCaption?: string;
}

interface FieldRendererProps {
  field: FormField;
}

function formatPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 10)}`;
}

export default function FieldRenderer({ field }: FieldRendererProps) {
  const { formData, setFormValue, errors, setError, clearError } = useFormStore();
  const value = formData[field.id] ?? '';
  const error = errors[field.id];
  const [uploading, setUploading] = useState(false);
  const inputId = `input-${field.id}`;

  const validationConfig = useMemo<FieldValidationConfig>(() => ({
    type: field.type,
    required: field.required,
    validation: field.validation,
  }), [field.type, field.required, field.validation]);

  const handleBlur = useCallback(() => {
    const result = validateField(value, validationConfig);
    if (!result.valid && result.message) setError(field.id, result.message);
    else clearError(field.id);
  }, [value, field.id, validationConfig, setError, clearError]);

  function handleChange(newValue: any) {
    if (error) clearError(field.id);
    setFormValue(field.id, newValue);
  }

  const cls = clsx(inputCls, error && inputError);
  const describedBy = [field.hint ? `${inputId}-hint` : null, error ? `${inputId}-error` : null].filter(Boolean).join(' ') || undefined;
  const common = { id: inputId, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined } as const;

  let control: React.ReactNode;

  switch (field.type) {
    case 'tel':
      control = (
        <input {...common} type="tel" inputMode="numeric" autoComplete="tel"
          value={formatPhone(String(value))}
          onChange={(e) => handleChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onBlur={handleBlur} placeholder={field.placeholder || '0912-345-678'} maxLength={12} className={cls} />
      );
      break;

    case 'taiwan_id':
    case 'resident_cert':
      control = (
        <input {...common} type="text" autoCapitalize="characters" autoComplete="off"
          value={String(value).toUpperCase()}
          onChange={(e) => handleChange(e.target.value.toUpperCase())}
          onBlur={handleBlur}
          placeholder={field.placeholder || (field.type === 'taiwan_id' ? 'A123456789' : 'AB12345678')}
          maxLength={10} className={cls} />
      );
      break;

    case 'email':
      control = (
        <input {...common} type="email" inputMode="email" autoComplete="email" value={value}
          onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur}
          placeholder={field.placeholder || 'name@example.com'} maxLength={field.validation?.maxLength} className={cls} />
      );
      break;

    case 'number':
      control = (
        <input {...common} type="number" inputMode="numeric" value={value}
          onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur}
          placeholder={field.placeholder} min={field.validation?.min} max={field.validation?.max} className={cls} />
      );
      break;

    case 'date':
      control = (
        <input {...common} type="date" value={value} onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur} className={cls} />
      );
      break;

    case 'textarea':
      control = (
        <textarea {...common} value={value} onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur}
          placeholder={field.placeholder} rows={3} maxLength={field.validation?.maxLength} className={clsx(textareaCls, error && inputError)} />
      );
      break;

    case 'select':
      control = (
        <div className="relative">
          <select {...common} value={value} onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur}
            className={clsx(cls, 'appearance-none pr-10', !value && 'text-faint')}>
            <option value="">請選擇</option>
            {field.options?.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <IconChevronDown size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
        </div>
      );
      break;

    case 'radio':
      control = (
        <div role="radiogroup" aria-labelledby={`${inputId}-label`} className="flex flex-col gap-2">
          {field.options?.map((opt) => {
            const on = value === opt.value;
            return (
              <label key={opt.value} className={clsx(
                'flex items-center gap-3 min-h-[52px] px-4 py-3 rounded-[14px] border-2 cursor-pointer transition-colors',
                on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
                error && !value && '!bg-bad-soft',
              )}>
                <input type="radio" name={field.id} value={opt.value} checked={on} onChange={() => handleChange(opt.value)} className="w-5 h-5 shrink-0" />
                <span className="flex-1 text-[15px] font-medium">{opt.label}</span>
                {opt.price !== undefined && <span className="tabular text-[15px] font-black">+NT$ {opt.price.toLocaleString('zh-TW')}</span>}
              </label>
            );
          })}
        </div>
      );
      break;

    case 'checkbox':
      return (
        <div id={`field-${field.id}`} className="flex flex-col gap-1.5">
          <label className={clsx(
            'flex items-start gap-3 px-4 py-3.5 rounded-[14px] cursor-pointer transition-colors',
            value ? 'bg-primary-soft' : 'bg-soft',
            error && '!bg-bad-soft',
          )}>
            <input type="checkbox" checked={Boolean(value)} onChange={(e) => handleChange(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" aria-describedby={describedBy} />
            <span className="text-[15px] leading-relaxed">
              {field.label}
              {field.required && <span className="text-bad-dot ml-1" aria-hidden="true">*</span>}
            </span>
          </label>
          {field.hint && <p id={`${inputId}-hint`} className={hintCls}>{field.hint}</p>}
          {error && <p id={`${inputId}-error`} role="alert" className="text-xs text-bad mt-0.5">{error}</p>}
        </div>
      );

    case 'image_display':
      return (
        <figure id={`field-${field.id}`} className="flex flex-col gap-2">
          {field.label && <figcaption className="text-[13px] font-bold">{field.label}</figcaption>}
          {field.imageUrl ? (
            <img src={field.imageUrl} alt={field.imageCaption || field.label} className="w-full rounded-tile" />
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 rounded-tile bg-soft text-muted">
              <IconImage size={26} />
              <span className="text-[13px]">{field.imageCaption || '參考圖片'}</span>
            </div>
          )}
          {field.imageCaption && field.imageUrl && <figcaption className="text-xs text-muted text-center">{field.imageCaption}</figcaption>}
        </figure>
      );

    case 'image_upload':
      control = (
        <div className="flex flex-col gap-2">
          <label htmlFor={`file-${field.id}`} className={clsx(
            'flex items-center gap-3.5 px-4 py-3.5 rounded-[14px] border-2 border-dashed cursor-pointer transition-colors',
            value ? 'border-ok-dot bg-ok-soft' : error ? 'border-bad-dot bg-bad-soft' : 'border-line bg-soft',
          )}>
            {value ? (
              <img src={String(value)} alt="已上傳的圖片" className="w-14 h-14 rounded-[10px] object-cover shrink-0" />
            ) : (
              <span className="w-14 h-14 rounded-[10px] bg-white text-muted inline-flex items-center justify-center shrink-0">
                {uploading ? <IconSpinner size={22} className="text-primary" /> : <IconCamera size={24} />}
              </span>
            )}
            <span className="flex-1 min-w-0 flex flex-col leading-snug">
              <span className="text-[15px] font-bold">{value ? '已上傳' : uploading ? '上傳中' : '拍照或選擇圖片'}</span>
              <span className="text-xs text-muted">{value ? '點一下可重新上傳' : '支援 JPG、PNG'}</span>
            </span>
            {value && <IconCheck size={20} className="text-ok-dot shrink-0" />}
          </label>
          <input
            id={`file-${field.id}`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const eventId = useFormStore.getState().event?.id;
              if (!eventId) { setError(field.id, '無法上傳：尚未載入活動資料'); return; }
              setUploading(true);
              const result = await api.uploadFile(file, eventId, field.id);
              setUploading(false);
              if (result.success && result.data) { clearError(field.id); handleChange(result.data.url); }
              else setError(field.id, result.error || '上傳失敗，請再試一次');
            }}
          />
        </div>
      );
      break;

    case 'image_radio':
      control = (
        <div role="radiogroup" aria-labelledby={`${inputId}-label`} className="grid grid-cols-2 gap-3">
          {field.options?.map((opt) => {
            const on = value === opt.value;
            return (
              <label key={opt.value} className={clsx(
                'flex flex-col rounded-[14px] overflow-hidden border-2 cursor-pointer transition-colors',
                on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
              )}>
                <input type="radio" name={field.id} value={opt.value} checked={on} onChange={() => handleChange(opt.value)} className="sr-only" />
                {opt.imageUrl ? (
                  <img src={opt.imageUrl} alt="" className="w-full h-28 object-cover" />
                ) : (
                  <div className="h-28 bg-white/60 flex items-center justify-center text-muted"><IconImage size={26} /></div>
                )}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <span className="text-[14px] font-bold">{opt.label}</span>
                  {on ? <IconCheck size={18} className="text-primary" /> : opt.price !== undefined ? <span className="tabular text-[13px] font-bold">+{opt.price}</span> : null}
                </div>
              </label>
            );
          })}
        </div>
      );
      break;

    default:
      control = (
        <input {...common} type="text" value={value} onChange={(e) => handleChange(e.target.value)} onBlur={handleBlur}
          placeholder={field.placeholder} maxLength={field.validation?.maxLength} className={cls} />
      );
  }

  return (
    <div id={`field-${field.id}`} className="flex flex-col">
      <label id={`${inputId}-label`} htmlFor={inputId} className={labelCls}>
        {field.label}
        {field.required && <span className="text-bad-dot ml-1" aria-hidden="true">*</span>}
        {field.labelEn && <span className="ml-2 font-normal text-faint">{field.labelEn}</span>}
      </label>
      {control}
      {field.hint && <p id={`${inputId}-hint`} className={hintCls}>{field.hint}</p>}
      {error && <p id={`${inputId}-error`} role="alert" className="text-xs text-bad mt-1.5">{error}</p>}
    </div>
  );
}
